<template>
  <transition name="modal-fade">
    <div v-if="visible" class="modal-overlay" @click.self="handleOverlayClick">
      <div class="modal-container" :class="type">
        <div class="modal-header">
          <h3 class="modal-title">{{ title }}</h3>
          <span class="modal-close" @click="handleClose">×</span>
        </div>
        <div class="modal-body">
          <p class="modal-message">{{ message }}</p>
        </div>
        <div class="modal-footer">
          <button 
            v-if="showCancel" 
            class="modal-btn modal-btn-cancel" 
            @click="handleCancel"
          >
            {{ cancelText }}
          </button>
          <button 
            class="modal-btn modal-btn-confirm" 
            @click="handleConfirm"
          >
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'Modal',
  data() {
    return {
      visible: false,
      title: '提示',
      message: '',
      type: 'info', // info, success, error, warning
      showCancel: false,
      confirmText: '确定',
      cancelText: '取消',
      onConfirm: null,
      onCancel: null,
      closeOnClickOverlay: true
    }
  },
  methods: {
    show(options = {}) {
      this.title = options.title || '提示'
      this.message = options.message || ''
      this.type = options.type || 'info'
      this.showCancel = options.showCancel || false
      this.confirmText = options.confirmText || '确定'
      this.cancelText = options.cancelText || '取消'
      this.onConfirm = options.onConfirm || null
      this.onCancel = options.onCancel || null
      this.closeOnClickOverlay = options.closeOnClickOverlay !== false
      this.visible = true
    },
    hide() {
      this.visible = false
      // 延迟重置数据，等待动画完成
      setTimeout(() => {
        this.reset()
      }, 300)
    },
    reset() {
      this.title = '提示'
      this.message = ''
      this.type = 'info'
      this.showCancel = false
      this.confirmText = '确定'
      this.cancelText = '取消'
      this.onConfirm = null
      this.onCancel = null
      this.closeOnClickOverlay = true
    },
    handleClose() {
      this.hide()
      if (this.onCancel) {
        this.onCancel()
      }
    },
    handleCancel() {
      this.hide()
      if (this.onCancel) {
        this.onCancel()
      }
    },
    handleConfirm() {
      if (this.onConfirm) {
        const result = this.onConfirm()
        // 如果返回 false，不关闭弹窗
        if (result !== false) {
          this.hide()
        }
      } else {
        this.hide()
      }
    },
    handleOverlayClick() {
      if (this.closeOnClickOverlay) {
        this.handleClose()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$VW: 1920;
$VWm: 750;
@function vPc($n) {
    @return ($n / $VW *100)+vw;
}

@function vRem($n) {
    @return ($n / 14)+rem;
}

$primary-color: #0e9ea1;
$primary-light: #0e9ea1;
$error-color: #f56c6c;
$warning-color: #e6a23c;
$success-color: #0e9ea1;

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.modal-container {
  background: #ffffff;
  border-radius: vPc(14);
  box-shadow: 0 vPc(10) vPc(40) rgba(0, 0, 0, 0.2);
  min-width: vPc(400);
  max-width: vPc(600);
  max-height: 80vh;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease;
  position: relative;

  &.success {
    .modal-header {
      border-bottom-color: $success-color;
      .modal-title {
        color: $success-color;
      }
    }
    .modal-btn-confirm {
      background: $success-color;
      &:hover {
        background: darken($success-color, 10%);
      }
    }
  }

  &.error {
    .modal-header {
      border-bottom-color: $error-color;
      .modal-title {
        color: $error-color;
      }
    }
    .modal-btn-confirm {
      background: $error-color;
      &:hover {
        background: darken($error-color, 10%);
      }
    }
  }

  &.warning {
    .modal-header {
      border-bottom-color: $warning-color;
      .modal-title {
        color: $warning-color;
      }
    }
    .modal-btn-confirm {
      background: $warning-color;
      &:hover {
        background: darken($warning-color, 10%);
      }
    }
  }

  &.info {
    .modal-header {
      border-bottom-color: $primary-color;
      .modal-title {
        color: $primary-color;
      }
    }
    .modal-btn-confirm {
      background: $primary-color;
      &:hover {
        background: darken($primary-color, 10%);
      }
    }
  }
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  padding: vPc(24) vPc(30);
  border-bottom: vPc(2) solid $primary-color;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f9fa;
}

.modal-title {
  font-size: vPc(20);
  font-weight: bold;
  color: $primary-color;
  margin: 0;
  line-height: 1.2;
}

.modal-close {
  font-size: vPc(32);
  color: #999;
  cursor: pointer;
  line-height: 1;
  transition: all 0.3s ease;
  width: vPc(30);
  height: vPc(30);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    color: $primary-color;
    background: rgba(14, 158, 161, 0.1);
    transform: rotate(90deg);
  }
}

.modal-body {
  padding: vPc(30);
  min-height: vPc(80);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-message {
  font-size: vPc(16);
  color: #333;
  line-height: 1.6;
  margin: 0;
  text-align: center;
  word-break: break-word;
}

.modal-footer {
  padding: vPc(20) vPc(30);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: vPc(16);
  border-top: vPc(1) solid #e8e8e8;
  background: #fafafa;
}

.modal-btn {
  padding: vPc(12) vPc(32);
  border: none;
  border-radius: vPc(6);
  font-size: vPc(16);
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;
  min-width: vPc(100);

  &:active {
    transform: scale(0.98);
  }
}

.modal-btn-cancel {
  background: #f5f5f5;
  color: #666;

  &:hover {
    background: #e8e8e8;
    color: #333;
  }
}

.modal-btn-confirm {
  background: $primary-color;
  color: #ffffff;

  &:hover {
    background: darken($primary-color, 10%);
  }
}

// 移动端适配
@media screen and (max-width: 768px) {
  .modal-container {
    min-width: 80%;
    max-width: 90%;
    margin: 0 vRem(20);
  }

  .modal-header {
    padding: vRem(20) vRem(24);
  }

  .modal-title {
    font-size: vRem(18);
  }

  .modal-close {
    font-size: vRem(28);
    width: vRem(28);
    height: vRem(28);
  }

  .modal-body {
    padding: vRem(24);
    min-height: vRem(60);
  }

  .modal-message {
    font-size: vRem(14);
  }

  .modal-footer {
    padding: vRem(16) vRem(24);
    gap: vRem(12);
  }

  .modal-btn {
    padding: vRem(10) vRem(24);
    font-size: vRem(14);
    min-width: vRem(80);
  }
}
</style>

